import React, { useEffect, useState } from "react";
import { SlidersHorizontal, Plus, Trash2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { PageHeader, Card, Badge, Loading, Modal, EmptyState } from "../components/ui";

const ENTITIES = [["employee", "Employee"], ["candidate", "Candidate"], ["asset", "Asset"]];
const TYPES = ["TEXT", "NUMBER", "DATE", "SELECT", "BOOLEAN"];

export default function CustomFields() {
  const [entity, setEntity] = useState("employee");
  const [fields, setFields] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", type: "TEXT", required: false, options: "" });

  const load = (e) => api.get(`/custom-fields?entity=${e}`).then(({ data }) => setFields(data));
  useEffect(() => { setFields(null); load(entity); }, [entity]);
  const create = async (ev) => {
    ev.preventDefault();
    try { await api.post("/custom-fields", { entity, label: form.label, type: form.type, required: form.required, options: form.options ? form.options.split(",").map((s) => s.trim()) : [] });
      setOpen(false); setForm({ label: "", type: "TEXT", required: false, options: "" }); load(entity); }
    catch (err) { window.alert(apiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (window.confirm("Delete this field?")) { await api.delete(`/custom-fields/${id}`); load(entity); } };
  if (!fields) return <Loading />;

  return (
    <div>
      <PageHeader title="Custom Fields" subtitle="Define your own fields on any record (form builder)">
        <button data-testid="add-field" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> New Field</button>
      </PageHeader>
      <div className="mb-6 flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {ENTITIES.map(([k, l]) => (
          <button key={k} data-testid={`cf-tab-${k}`} onClick={() => setEntity(k)} className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium ${entity === k ? "border-accent text-accent" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>{l}</button>
        ))}
      </div>
      {fields.length === 0 ? <EmptyState icon={SlidersHorizontal} title="No custom fields" hint={`Add a custom field for ${entity} records.`} /> : (
        <Card className="overflow-hidden"><div className="overflow-x-auto">
          <table className="w-full" data-testid="cf-table">
            <thead><tr><th className="th">Label</th><th className="th">Key</th><th className="th">Type</th><th className="th">Required</th><th className="th">Options</th><th className="th"></th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {fields.map((f) => (
                <tr key={f.id} data-testid={`cf-${f.id}`}>
                  <td className="td font-medium text-slate-800 dark:text-slate-100">{f.label}</td>
                  <td className="td font-mono text-xs text-slate-400">{f.key}</td>
                  <td className="td"><Badge tone="info">{f.type}</Badge></td>
                  <td className="td text-sm">{f.required ? "Yes" : "No"}</td>
                  <td className="td text-sm text-slate-500">{(f.options || []).join(", ") || "—"}</td>
                  <td className="td"><button data-testid={`cf-del-${f.id}`} onClick={() => del(f.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={15} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></Card>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={`New ${ENTITIES.find((e) => e[0] === entity)[1]} Field`}
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="cf-form" data-testid="cf-submit" className="btn-accent">Create</button></>}>
        <form id="cf-form" onSubmit={create} className="space-y-4">
          <div><label className="label">Label *</label><input required data-testid="cf-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Type</label><select data-testid="cf-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
            <label className="flex items-end gap-2 pb-2 text-sm text-slate-600 dark:text-slate-300"><input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} /> Required</label>
          </div>
          {form.type === "SELECT" && <div><label className="label">Options (comma-separated)</label><input data-testid="cf-options" value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} className="input" placeholder="Option A, Option B" /></div>}
        </form>
      </Modal>
    </div>
  );
}
